import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { useAuth } from '@/lib/auth';
import { AuthSplitLayout } from '@/pages/Login';

// NEW page for the email verification feature. The verification email links
// here as https://.../verify-email?token=... — this calls the backend once
// on mount and shows the result. Also used as the verification landing page
// for the mobile app's signups (see the mobile-app README note), so it
// deliberately doesn't assume a browser session exists.
export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { verifyEmail } = useAuth();
  const [status, setStatus] = useState('checking'); // checking | success | error
  const [message, setMessage] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode/double-mount guard — don't burn the token twice
    ran.current = true;
    if (!token) {
      setStatus('error');
      setMessage('This verification link is missing its token.');
      return;
    }
    verifyEmail(token)
      .then((data) => {
        setStatus('success');
        setMessage(data.message || 'Email verified. You can now log in.');
      })
      .catch((err) => {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'This link is invalid or has expired.');
      });
  }, [token, verifyEmail]);

  return (
    <AuthSplitLayout mode="customer">
      <div className="lg:hidden mb-10"><Logo /></div>

      {status === 'checking' && (
        <>
          <Loader2 className="text-volt animate-spin mb-4" size={28} />
          <h1 className="font-heading font-black text-3xl uppercase tracking-tight">Verifying…</h1>
        </>
      )}

      {status === 'success' && (
        <>
          <CheckCircle2 className="text-volt mb-4" size={28} />
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight leading-none">Email verified.</h1>
          <p className="mt-3 text-white/60 text-sm">{message}</p>
          <Link to="/login" className="mt-8 inline-block text-volt text-sm font-bold uppercase tracking-widest hover:underline" data-testid="verify-email-login-link">
            Continue to sign in →
          </Link>
        </>
      )}

      {status === 'error' && (
        <>
          <XCircle className="text-[#FF3B30] mb-4" size={28} />
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight leading-none">Link didn't work.</h1>
          <p className="mt-3 text-white/60 text-sm">{message}</p>
          <Link to="/register" className="mt-8 inline-block text-volt text-sm font-bold uppercase tracking-widest hover:underline">
            ← Back to sign up
          </Link>
        </>
      )}
    </AuthSplitLayout>
  );
}
